import React, { useState } from 'react';
import { authService } from '../../../services/authService';
import Icon from '../../../components/AppIcon';

const PasswordResetForm = ({ onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validatePassword = (password) => {
    const requirements = {
      length: password?.length >= 8,
      uppercase: /[A-Z]/?.test(password),
      lowercase: /[a-z]/?.test(password),
      number: /[0-9]/?.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/?.test(password)
    };

    return requirements;
  };

  const passwordRequirements = validatePassword(newPassword);
  const isPasswordValid = Object.values(passwordRequirements)?.every(req => req);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const isFormValid = isPasswordValid && passwordsMatch;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!isFormValid) {
      setError('Please ensure all password requirements are met and passwords match');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await authService?.updatePassword(newPassword);

      if (updateError) {
        setError(updateError);
        setLoading(false);
        return;
      }

      // Success - notify parent component
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to reset password. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-foreground mb-2">
          New Password
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon name="Lock" size={20} color="var(--color-muted-foreground)" />
          </div>
          <input
            id="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e?.target?.value)}
            placeholder="Enter new password"
            className="w-full pl-10 pr-12 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
            disabled={loading}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
          >
            <Icon name={showNewPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
      </div>
      {newPassword && (
        <div className="p-4 bg-muted/50 rounded-lg border border-border space-y-2">
          <p className="text-sm font-medium text-foreground mb-2">Password Requirements:</p>
          <div className="space-y-1">
            <PasswordRequirement met={passwordRequirements?.length} text="At least 8 characters" />
            <PasswordRequirement met={passwordRequirements?.uppercase} text="One uppercase letter" />
            <PasswordRequirement met={passwordRequirements?.lowercase} text="One lowercase letter" />
            <PasswordRequirement met={passwordRequirements?.number} text="One number" />
            <PasswordRequirement met={passwordRequirements?.special} text="One special character" />
          </div>
        </div>
      )}
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
          Confirm New Password
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon name="Lock" size={20} color="var(--color-muted-foreground)" />
          </div>
          <input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e?.target?.value)}
            placeholder="Confirm new password"
            className={
              `w-full pl-10 pr-12 py-3 bg-background border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 transition-all ${
                confirmPassword && !passwordsMatch
                  ? 'border-destructive focus:ring-destructive/20' :'border-border focus:ring-primary/20'
              }`
            }
            disabled={loading}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        {confirmPassword && !passwordsMatch && (
          <p className="mt-2 text-sm text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            Passwords do not match
          </p>
        )}
        {confirmPassword && passwordsMatch && (
          <p className="mt-2 text-sm text-success flex items-center gap-1">
            <Icon name="CheckCircle" size={14} />
            Passwords match
          </p>
        )}
      </div>
      <button
        type="submit"
        disabled={!isFormValid || loading}
        className={
          `w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            !isFormValid || loading
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md'
          }`
        }
      >
        {loading ? (
          <>
            <Icon name="Loader2" size={20} className="animate-spin" />
            Resetting Password...
          </>
        ) : (
          <>
            <Icon name="Key" size={20} />
            Reset Password
          </>
        )}
      </button>
    </form>
  );
};

const PasswordRequirement = ({ met, text }) => (
  <div className="flex items-center gap-2 text-sm">
    {met ? (
      <Icon name="CheckCircle" size={16} color="#10b981" />
    ) : (
      <Icon name="Circle" size={16} color="var(--color-muted-foreground)" />
    )}
    <span className={met ? 'text-success' : 'text-muted-foreground'}>{text}</span>
  </div>
);

export default PasswordResetForm;